import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  alpha, // Added alpha for dynamic sidebar colors
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Assessment as ReportsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AdminPanelSettings as AdminIcon,
  GroupWork as GroupWorkIcon,
  CurrencyRupee as MoneyIcon,
  DesignServices as ServicesIcon,
  Security as SecurityIcon,
  ConfirmationNumber as TicketIcon,
  ShoppingCart as PurchaseOrderIcon,
  ExpandLess,
  ExpandMore,
  SpaceDashboard as GSTDashboardIcon,
  ReceiptLong as GSTInvoiceIcon,
  ShoppingCartCheckout as GSTPurchaseIcon,
  Description as GSTR1Icon,
  Summarize as GSTR3BIcon,
  Analytics as OverviewIcon,
  FiberManualRecord as DotIcon,
  Business as OrgIcon,
  PlayCircleOutline as DemoIcon,
} from '@mui/icons-material';
import { useRoleBasedAccess } from '../../hooks/useRoleBasedAccess';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveFileUrl } from '../../utils/url';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileToggle: () => void;
}

const drawerWidth = 280;
const collapsedWidth = 70;

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileToggle
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const permissions = useRoleBasedAccess();
  const { user } = useAuth();
  const { permissions: livePermissions, isAdmin, role } = usePermissions();

  const [reportsOpen, setReportsOpen] = React.useState(false);
  const [superAdminOpen, setSuperAdminOpen] = React.useState(false);

  React.useEffect(() => {
    if (location.pathname === '/reports' && location.hash) {
      setReportsOpen(true);
    }
    if (location.pathname === '/super-admin') {
      setSuperAdminOpen(true);
    }
  }, [location.pathname, location.hash]);

  const isSubUserWithNoPerms = !isAdmin && role !== 'ADMIN' && livePermissions.length === 0;

  const mainMenuItems = isSubUserWithNoPerms ? [] : [
    { 
      text: 'Control Panel', 
      icon: <SecurityIcon />, 
      path: '/super-admin', 
      visible: permissions.canViewSuperAdmin, 
      id: 'tour-nav-super-admin',
      subItems: [
        { text: 'Organizations', path: '/super-admin#organizations', icon: <OrgIcon fontSize="small" /> },
        { text: 'Support Tickets', path: '/super-admin#tickets', icon: <TicketIcon fontSize="small" /> },
        { text: 'Demo Requests', path: '/super-admin#demos', icon: <DemoIcon fontSize="small" /> },
      ]
    },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', visible: permissions.canViewDashboard, id: 'tour-nav-dashboard' },
    { text: 'Bills', icon: <ReceiptIcon />, path: '/bills', visible: permissions.canViewBills, id: 'tour-nav-bills' },
    { text: 'Customers', icon: <PeopleIcon />, path: '/customers', visible: permissions.canViewCustomers, id: 'tour-nav-customers' },
    { text: 'Products', icon: <InventoryIcon />, path: '/products', visible: permissions.canViewProducts, id: 'tour-nav-products' },
    { text: 'Suppliers', icon: <GroupWorkIcon />, path: '/suppliers', visible: permissions.canViewSuppliers, id: 'tour-nav-suppliers' },
    { text: 'Services', icon: <ServicesIcon />, path: '/services', visible: permissions.canManageServices, id: 'tour-nav-services' },
    { text: 'Service Tickets', icon: <TicketIcon />, path: '/service-tickets', visible: permissions.canManageServiceTickets, id: 'tour-nav-service-tickets' },
    { text: 'Purchase Orders', icon: <PurchaseOrderIcon />, path: '/purchase-orders', visible: permissions.canManagePurchaseOrders, id: 'tour-nav-purchase-orders' },
    { text: 'Expenses', icon: <MoneyIcon />, path: '/expenses', visible: permissions.canManageExpenses, id: 'tour-nav-expenses' },
    {
      text: 'Reports',
      icon: <ReportsIcon />,
      path: '/reports',
      visible: permissions.canViewReports,
      id: 'tour-nav-reports',
      subItems: [
        { text: 'Business Overview', path: '/reports', icon: <OverviewIcon fontSize="small" /> },
        { text: 'GST Dashboard', path: '/reports#dashboard', icon: <GSTDashboardIcon fontSize="small" /> },
        { text: 'GST Invoices', path: '/reports#invoices', icon: <GSTInvoiceIcon fontSize="small" /> },
        { text: 'GST Purchases', path: '/reports#purchases', icon: <GSTPurchaseIcon fontSize="small" /> },
        { text: 'GSTR-1 Report', path: '/reports#gstr1', icon: <GSTR1Icon fontSize="small" /> },
        { text: 'GSTR-3B Report', path: '/reports#gstr3b', icon: <GSTR3BIcon fontSize="small" /> },
        { text: 'GST Filing Hub', path: '/gst-filing', icon: <GSTR1Icon fontSize="small" /> }
      ]
    },

    { text: 'Admin Panel', icon: <AdminIcon />, path: '/admin', visible: permissions.canViewAdminPanel, id: 'tour-nav-admin' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings', visible: permissions.canManageSettings, id: 'tour-nav-settings' }
  ];

  const visibleMenuItems = mainMenuItems.filter(item => item.visible);

  const renderMenuItem = (item: { text: string; icon: JSX.Element; path: string; id: string; subItems?: Array<{ text: string; path: string; icon?: JSX.Element }> }) => {
    // Check if the current path matches the menu item's path
    const isActive = item.path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(item.path);

    const hasSubItems = item.subItems && item.subItems.length > 0;

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ px: 1 }} id={item.id}>
          <ListItemButton
            component={hasSubItems ? "div" : Link}
            to={hasSubItems ? undefined : item.path}
            selected={isActive && !hasSubItems}
            sx={{
              minHeight: 48,
              justifyContent: collapsed && !isMobile ? 'center' : 'initial',
              px: 2.5,
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              transition: 'all 0.2s ease',
              '& .MuiListItemIcon-root': {
                color: `${theme.palette.text.secondary} !important`, // Soft Light Black / Grey
                '& .icon-bg': {
                  backgroundColor: 'transparent',
                  borderRadius: '50%',
                }
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08), // Slightly more visible on click
                borderLeft: `5px solid ${theme.palette.primary.main}`,
                marginLeft: '-1px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12)
                },
                '& .MuiListItemIcon-root': {
                  color: `${theme.palette.primary.main} !important`, // Selective branding
                  '& .icon-bg': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: '50%',
                  }
                },
                '& .MuiListItemText-primary': {
                  fontWeight: 700, // Bold on active
                  color: `${theme.palette.text.primary} !important`, // DARKER on active (not faded)
                },
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                '& .MuiListItemIcon-root': { color: theme.palette.primary.main } // Flash brand color on hover
              },
            }}
            onClick={() => {
              if (hasSubItems) {
                if (item.text === 'Reports') setReportsOpen(!reportsOpen);
                if (item.text === 'Control Panel') setSuperAdminOpen(!superAdminOpen);
              } else if (isMobile) {
                onMobileToggle();
              }
            }}
          >
            <ListItemIcon sx={{
              minWidth: 0,
              mr: collapsed && !isMobile ? 0 : 3,
              justifyContent: 'center',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              color: 'inherit' // Inherit from the SX rule above
            }}>
              <Box
                className="icon-bg"
                sx={{
                  position: 'absolute',
                  width: 32,
                  height: 32,
                  zIndex: 0,
                  transition: 'all 0.2s ease',
                }}
              />
              <Box sx={{ zIndex: 1, display: 'flex' }}>
                {item.icon}
              </Box>
            </ListItemIcon>
            {(!collapsed || isMobile) && (
              <ListItemText primary={item.text} sx={{ opacity: collapsed && !isMobile ? 0 : 1 }} />
            )}
            {hasSubItems && (!collapsed || isMobile) && (
              ((item.text === 'Reports' && reportsOpen) || (item.text === 'Control Panel' && superAdminOpen)) ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {hasSubItems && (!collapsed || isMobile) && (
          <Collapse in={(item.text === 'Reports' && reportsOpen) || (item.text === 'Control Panel' && superAdminOpen)} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pb: 1 }}>
              {item.subItems?.map((subItem) => {
                const currentHash = location.hash || '';
                const subHash = subItem.path.split('#')[1] ? '#' + subItem.path.split('#')[1] : '';
                const isSubSelected = location.pathname === item.path && currentHash === subHash;
                return (
                  <ListItemButton
                    key={subItem.text}
                    component={Link}
                    to={subItem.path}
                    selected={isSubSelected}
                    onClick={isMobile ? onMobileToggle : undefined}
                    sx={{
                      pl: 6,
                      my: 0.25,
                      mx: 2,
                      borderRadius: 2, // smooth rounded pills
                      minHeight: 38, // tighter padding
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        // small accent line indicator on the left for selected
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -4,
                          top: '15%',
                          height: '70%',
                          width: 3,
                          borderRadius: 3,
                          bgcolor: theme.palette.primary.main,
                        },
                      },
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 36,
                      color: isSubSelected ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.7),
                      transition: 'color 0.2s ease',
                      '& svg': { fontSize: '1.25rem' } // 20px icons
                    }}>
                      {subItem.icon || <DotIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={subItem.text}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: isSubSelected ? theme.palette.primary.main : 'text.secondary',
                        fontWeight: isSubSelected ? 600 : 500,
                        letterSpacing: '0.2px'
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box className="tour-sidebar" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: collapsed && !isMobile ? 'center' : 'space-between', minHeight: 64, borderBottom: `1px solid ${theme.palette.divider}` }}>
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            {user?.logoUrl ? (
              <img
                src={resolveFileUrl(user.logoUrl)}
                alt="Logo"
                style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
              />
            ) : (
              <Box sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                borderRadius: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}>
                <GroupWorkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                fontSize: '1.15rem',
                fontFamily: 'Outfit, sans-serif',
                color: 'text.primary',
                letterSpacing: '-0.5px',
                lineHeight: 1.2
              }}
            >
              {user?.companyName || 'My Business'}
            </Typography>
          </Box>
        )}
        {collapsed && !isMobile && user?.logoUrl && (
          <img
            src={resolveFileUrl(user.logoUrl)}
            alt="Logo"
            style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
          />
        )}
        <IconButton onClick={isMobile ? onMobileToggle : onToggle} size="small">
          {collapsed && !isMobile ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, pt: 1, overflowY: 'auto' }}>
        {visibleMenuItems.map(renderMenuItem)}
      </List>

      <Box sx={{ p: 2, textAlign: 'center', mt: 'auto', borderTop: `1px solid ${theme.palette.divider}` }}>
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.2 }}>
              <img src="/logo.svg" alt="BillSoft" style={{ height: '14px', opacity: 0.6 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                © 2025 BillSoft
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', opacity: 0.7 }}>
              Powered by AGB Technologies
            </Typography>
          </Box>
        )}
        {collapsed && !isMobile && (
          <img src="/logo.svg" alt="BS" style={{ height: '14px', opacity: 0.4 }} />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '@media print': { display: 'none !important' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: theme.palette.background.paper, // Solid white/background for mobile sidebar
              backgroundImage: 'none',
              '@media print': { display: 'none !important' }
            }
          }}
        >
          {drawer}
        </Drawer>
      )}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: collapsed ? collapsedWidth : drawerWidth,
            flexShrink: 0,
            '@media print': { display: 'none !important' },
            '& .MuiDrawer-paper': {
              width: collapsed ? collapsedWidth : drawerWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              backgroundColor: alpha(theme.palette.primary.main, 0.02), // "Dudhiya" tint background
              backgroundImage: 'none',
              borderRight: `1px solid ${theme.palette.divider}`,
              '@media print': { display: 'none !important' }
            }
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
